import { useEffect, useMemo, useCallback, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useMacros } from '@/hooks/useMacros';
import { setFocusIndex } from '@/redux/macros/actions';
import { focusIndexSelector } from '@/redux/macros/selectors';

// The active quick-response command token: a "/" at a word boundary (input start
// or after whitespace) plus its single-token query, anchored to the caret end.
// Single source of truth for both reading the query and stripping the token.
const QUICK_RESPONSE_TOKEN = /(?:^|\s)(\/[^\s/]*)$/;

/**
 * Returns the query (text after the "/", possibly "") when the caret sits inside
 * a command token, otherwise null. A "/" glued to a preceding character — a date
 * (12/05), fraction (1/2), or URL — is a literal and returns null.
 */
export const getQuickResponseQuery = (textBeforeCaret: string): string | null =>
  textBeforeCaret.match(QUICK_RESPONSE_TOKEN)?.[1].slice(1) ?? null;

/** Removes the active command token, keeping any text (incl. the boundary space) before it. */
export const stripQuickResponseToken = (textBeforeCaret: string): string =>
  textBeforeCaret.replace(QUICK_RESPONSE_TOKEN, (full, token) => full.slice(0, -token.length));

export const useQuickResponses = (query: string | null) => {
  const dispatch = useDispatch();
  const { macros } = useMacros();
  const focusIndex = useSelector(focusIndexSelector);

  const filteredOptions = useMemo(() => {
    const searchTerm = (query ?? '').toLowerCase();
    return macros?.filter((option) =>
      `${option?.name} ${option?.text}`.toLowerCase().includes(searchTerm)
    );
  }, [query, macros]);

  useEffect(() => {
    dispatch(setFocusIndex(0));
  }, [dispatch, filteredOptions]);

  const focusIndexRef = useRef(focusIndex);
  focusIndexRef.current = focusIndex;

  const navigateUp = useCallback(() => {
    const length = filteredOptions?.length ?? 0;
    if (length === 0) {
      return;
    }
    const current = focusIndexRef.current;
    dispatch(setFocusIndex(current === 0 ? length - 1 : current - 1));
  }, [dispatch, filteredOptions?.length]);

  const navigateDown = useCallback(() => {
    const length = filteredOptions?.length ?? 0;
    if (length === 0) {
      return;
    }
    const current = focusIndexRef.current;
    dispatch(setFocusIndex(current === length - 1 ? 0 : current + 1));
  }, [dispatch, filteredOptions?.length]);

  const selectedText = filteredOptions?.[focusIndex]?.text;

  return { filteredOptions, focusIndex, navigateUp, navigateDown, selectedText };
};
